import React, {Fragment, useMemo} from 'react';
import {QuestionModelV3} from '@albert-io/models';
import {useShallow} from 'zustand/react/shallow';
import Divider from './Divider.react';
import {useQuestionStore} from 'client/components/QuestionTypes/V2/useQuestionStores';
import {MovableItem} from '../../shared/MovableItem.react';

interface Props {
  item: {id: string; value: string; placedInSlot?: string | null};
  index: number;
  handleItemClick: (itemId: string, event: React.MouseEvent) => void;
  handleDividerClick: (dividerIndex: number) => void;
  question: QuestionModelV3;
}

export const Item = ({item, handleItemClick, handleDividerClick, index, question}: Props) => {
  const useStoreHook = useQuestionStore<'ordering'>(question);

  const {activeItemId, isDragging, hoveredDividerId, draggedItemId, isSubmitted, correctAnswer} =
    useStoreHook(
      useShallow((state) => ({
        activeItemId: state.activeItemId,
        isDragging: state.isDragging,
        hoveredDividerId: state.hoveredDividerId,
        draggedItemId: state.draggedItemId,
        isSubmitted: state.isSubmitted,
        correctAnswer: state.correctAnswer
      }))
    );

  const isCorrect = useMemo(() => {
    if (!correctAnswer) return false;

    // Slots define correct positions: position i is correct when the item here
    // matches validResponse[slots[i].id]
    const questionSlots =
      question?.getQuestionTypeAttributes()?.getIn(['ordering', 'slots'], []).toJS() || [];
    const slotId = questionSlots[index]?.id;

    return slotId ? correctAnswer[slotId] === item.id : false;
  }, [correctAnswer, item.id, index, question]);

  return (
    <Fragment key={`${question?.getId?.()}-${item.id}`}>
      <MovableItem
        key={item.id}
        item={item}
        isActive={activeItemId === item.id}
        isDragging={isDragging && draggedItemId === item.id}
        isSubmitted={isSubmitted}
        onClick={(e) => handleItemClick(item.id, e)}
        activeItemId={activeItemId}
        isCorrect={isCorrect}
        showIcon={false}
      />

      {/* Divider after each item */}
      {!isSubmitted && (
        <Divider
          id={`divider-${index + 1}`}
          isVisible={isDragging || activeItemId !== null}
          isHovered={hoveredDividerId === `divider-${index + 1}`}
          onClick={() => handleDividerClick(index + 1)}
        />
      )}
    </Fragment>
  );
};
